import React, { useEffect, useRef } from "react";
import Button from "../Button";
import { useAskWaiting } from "../../hooks/useAskWaiting";
import { dismissAsk, hostAskReminder, resumeAsk, waitingNote } from "../../utils/askReturn";
import classes from "./record.module.scss";

interface SaveBarProps {
    isDirty: boolean;
    /** A record that does not exist yet: the bar reads "Not saved yet" rather than counting changes. */
    isNew?: boolean;
    /** "3 unsaved changes"; omit for a plain "Unsaved changes". */
    dirtyCount?: number;
    /** Which parts are dirty — rendered "Unsaved: Info · Movements". */
    dirtyLabels?: string[];
    isSaving?: boolean;
    /** Saves everything dirty. Pages orchestrate multi-slice saves here. */
    onSave?: () => void;
    /**
     * Submit this form (by id) instead of calling onSave — Save becomes the
     * form's submit button and Ctrl+S submits it, so the form's own
     * validation and onSubmit run.
     */
    formId?: string;
    onDiscard?: () => void;
    saveLabel?: string;
    discardLabel?: string;
    /** Extra muted text — "Ctrl + S saves", a validation note. */
    hint?: React.ReactNode;
}

const submitForm = (formId: string) => {
    const form = document.getElementById(formId);
    if (form instanceof HTMLFormElement) form.requestSubmit();
};

/**
 * Pinned to the bottom of the page while there are unsaved changes:
 * "N unsaved changes · Discard · Save". Ctrl+S / ⌘S saves while it is shown.
 * Presentational — the page owns what gets saved.
 */
const SaveBar: React.FC<SaveBarProps> = ({
    isDirty, isNew = false, dirtyCount, dirtyLabels, isSaving = false, onSave, formId, onDiscard,
    saveLabel = "Save changes", discardLabel = "Discard", hint,
}) => {
    const saveRef = useRef<() => void>(() => undefined);
    saveRef.current = () => {
        if (formId) submitForm(formId);
        else onSave?.();
    };

    const isShown = isDirty || isSaving;

    useEffect(() => {
        if (!isDirty || isSaving) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                saveRef.current();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isDirty, isSaving]);

    // Ask waiting on this form (2026-10-15): the reminder sits in the bar, so no pill floats over Save.
    // (closed on purpose = quiet: only the header and search remind)
    const { waiting } = useAskWaiting();
    const showsAsk = isShown && !!waiting && !waiting.quiet;
    useEffect(() => (showsAsk ? hostAskReminder() : undefined), [showsAsk]);

    if (!isShown) return null;

    const countText = isNew
        ? "Not saved yet"
        : dirtyCount && dirtyCount > 0
        ? `${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}`
        : "Unsaved changes";

    return (
        <div className={classes.saveBar} role="region" aria-label="Unsaved changes">
            <div className={classes.saveBarState}>
                <span className={classes.saveBarCount}>{isSaving && !isDirty ? "Saving…" : countText}</span>
                {!isNew && dirtyLabels && dirtyLabels.length > 0 && (
                    <span className={classes.saveBarLabels}>Unsaved: {dirtyLabels.join(" · ")}</span>
                )}
                {hint && <span className={classes.saveBarHint}>{hint}</span>}
                {showsAsk && waiting && (
                    <span className={classes.saveBarAsk} role="status" aria-label="Ask CRX is waiting">
                        <i className="bi bi-stars" aria-hidden="true" />
                        <span className={classes.saveBarAskTitle}>{waiting.filled ? "Filled by Ask" : "Ask is waiting"}</span>
                        <span className={classes.saveBarAskNote}>{waitingNote(waiting)}</span>
                        <button type="button" className={`btn-unstyled ${classes.saveBarAskBack}`} onClick={resumeAsk}
                            title={`Open Ask at the same answer${waiting.title ? ` (“${waiting.title}”)` : ""} — your changes here stay. Saving brings it back too.`}>
                            Back to Ask
                        </button>
                        <button type="button" className={`btn-unstyled ${classes.saveBarAskDismiss}`} onClick={dismissAsk}
                            title="Stop reminding me — saving won't bring Ask back. The chat stays in Ask's Chats.">
                            Not now
                        </button>
                    </span>
                )}
            </div>
            <div className={classes.saveBarActions}>
                {onDiscard && (
                    <Button type="button" variant="ghost" size="md" title="Throw away the unsaved changes" disabled={isSaving} onClick={onDiscard}>
                        {discardLabel}
                    </Button>
                )}
                <Button
                    type={formId ? "submit" : "button"}
                    form={formId}
                    variant="primary"
                    size="md"
                    icon="check2"
                    title="Save the changes (Ctrl+S)"
                    loading={isSaving}
                    loadingText="Saving…"
                    onClick={formId ? undefined : onSave}
                >
                    {saveLabel}
                </Button>
            </div>
        </div>
    );
};

export default SaveBar;
